// Scroll-spy for the game guide's table-of-contents sidebar.
//
// Highlights the TOC link whose section is currently in view as the user
// scrolls. Safe no-op on pages without a `.game-info-toc`, so it can live in
// the shared landing-page bundle.

interface TocEntry {
  link: HTMLAnchorElement;
  target: HTMLElement;
}

// A section becomes "current" once its top passes this many pixels below the
// top of the viewport. Chosen to sit just under the sticky sidebar's offset so
// the highlight tracks what the reader is actually looking at.
const ACTIVATION_OFFSET = 140;

function initTocScrollSpy(): void {
  const toc = document.querySelector<HTMLElement>('.game-info-toc');
  if (!toc) return;

  const entries: TocEntry[] = Array.from(
    toc.querySelectorAll<HTMLAnchorElement>('a[href^="#"]')
  )
    .map((link): TocEntry | null => {
      const id = link.getAttribute('href')?.slice(1) ?? '';
      const target = id ? document.getElementById(id) : null;
      return target ? { link, target } : null;
    })
    .filter((entry): entry is TocEntry => entry !== null);

  if (entries.length === 0) return;

  let activeLink: HTMLAnchorElement | null = null;

  function setActive(link: HTMLAnchorElement): void {
    if (link === activeLink) return;
    activeLink?.classList.remove('is-active');
    link.classList.add('is-active');
    activeLink = link;
  }

  function updateActive(): void {
    // Walk down the sections; the last one whose top has crossed the
    // activation line is the current section.
    let current = entries[0];
    for (const entry of entries) {
      if (entry.target.getBoundingClientRect().top - ACTIVATION_OFFSET <= 0) {
        current = entry;
      }
    }

    // Snap to the final section once scrolled to the bottom, so the last item
    // (often a short FAQ) can still become active.
    const scrolledToBottom =
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - 2;
    if (scrolledToBottom) {
      current = entries[entries.length - 1];
    }

    setActive(current.link);
  }

  let ticking = false;
  function onScroll(): void {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      updateActive();
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  updateActive();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTocScrollSpy);
} else {
  initTocScrollSpy();
}
